<script lang="ts" setup>
import { ref, computed, PropType } from 'vue'
import { IllustDetail } from '../../../libs/models'
import { useApi } from '../libs/api'
import Modal from './Modal.vue'
import Loading from './Loading.vue'
import { getApiErrorMessage } from '../libs/apiErrors'

const props = defineProps({
  illust: { type: Object as PropType<IllustDetail>, required: true }
})

const emit = defineEmits(['close', 'updated'])

const api = useApi()
const isSubmitting = ref(false)
const title = ref(props.illust.title)
const desc = ref(props.illust.desc || '')
const tagInput = ref(props.illust.tags.join(' '))
const pinned = ref(props.illust.pinned)
const tags = computed(() => tagInput.value.trim().split(/\s+/).filter(tag => tag.length > 0).map(v => v.slice(0, 20)).slice(0, 5))

const onSubmit = () => {
  if (!title.value.trim() || isSubmitting.value) return
  isSubmitting.value = true
  
  api.put(`/illusts/${props.illust.id}`, {
    title: title.value.trim(),
    desc: desc.value.trim() || null,
    tags: tags.value,
    pinned: pinned.value
  }).then(({ data }) => {
    props.illust.title = data.title
    props.illust.desc = data.desc
    props.illust.tags = data.tags
    props.illust.pinned = data.pinned
    emit('updated')
    emit('close')
  }).catch(error => {
    console.error('Failed to update illust:', error)
    alert(getApiErrorMessage(error, 'イラストの更新に失敗しました'))
  }).finally(() => {
    isSubmitting.value = false
  })
}
</script>

<template>
  <Modal @close="$emit('close')">
    <div class="ModalContent">
      <div class="ModalTitle">{{ l('イラストを編集') }}</div>
      <form @submit.prevent="onSubmit">
        <input 
          type="text" 
          v-model="title" 
          :placeholder="l('タイトル')" 
          maxlength="30"
          :disabled="isSubmitting"
          required
        />
        <textarea
          class="Caption"
          v-model="desc"
          :placeholder="l('キャプション')"
          maxlength="255"
          :disabled="isSubmitting"
        />
        <input 
          type="text" 
          v-model="tagInput" 
          :placeholder="l('タグ（半角スペース区切り）')" 
          maxlength="255"
          :disabled="isSubmitting"
        />
        <div class="Tags" v-if="tags.length > 0">
          <a href="#" class="Tag" v-for="(tag, i) in tags" :key="i" @click.prevent>{{ tag }}</a>
        </div>
        <div class="Pin">
          <label><input type="checkbox" v-model="pinned"> {{ l('イラスト一覧でピン止めする') }}</label><br>
        </div>
        <Loading v-if="isSubmitting" :size="24" />
        <button v-else type="submit" :class="{ disabled: !title.trim() }">{{ l('更新') }}</button>
        <button type="button" class="cancel" @click="$emit('close')">{{ l('キャンセル') }}</button>
      </form>
    </div>
  </Modal>
</template>

<style lang="scss" scoped>
input + input {
  margin-top: 10px;
}
.Caption + input {
  margin-top: 10px;
}
.Caption {
  min-height: 80px;
  margin-top: 10px;
  resize: vertical;
}
button + button {
  margin-top: 16px;
}
.Pin {
  margin-top: 15px;
  margin-bottom: 15px;
  text-align: center;
}
</style>
